import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { RestoreSessionService } from './core/services/restore-session.service';
import { FlowType } from './core/interfaces/restore.interfaces';

const FLOW_ROUTES: Record<FlowType, string> = {
  restoring_access: '/restoring-access/intro',
  account_recovery: '/restoring-access/intro',
  change_phone_number: '/change-phone-number/intro',
  additional_phone_number: '/additional-phone-number/intro',
  upgrade_additional_to_main_phone_number: '/upgrade-additional-to-main/intro',
  rent: '/rent',
};

function isValidFlowType(v: string | null): v is FlowType {
  return v !== null && Object.prototype.hasOwnProperty.call(FLOW_ROUTES, v);
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit {
  readonly version = '1.1.3';

  private router = inject(Router);
  private document = inject(DOCUMENT);
  private session = inject(RestoreSessionService);

  ngOnInit(): void {
    const search = this.document.defaultView?.location.search ?? '';
    const params = new URLSearchParams(search);
    const token = params.get('token');
    const serviceId = params.get('service_id');
    const applicationId = params.get('application_id');
    const fcm = params.get('fcm');
    const rawTypeParam = params.get('type');
    const typeParam = rawTypeParam?.trim() ?? null;
    const isNewDeepLink = rawTypeParam !== null;

    const phone = params.get('phone');
    const number = params.get('number');
    const additional = params.get('additional');

    const isRentLink = typeParam === 'rent';
    let rentLinkValid = true;
    if (isRentLink) {
      // Rent links identify a new session; never inherit credentials or mileage from another link.
      this.session.clearToken();
      this.session.clearApplicationId();
      this.session.clearLastMileage();
      const rawMileage = params.get('last_mileage');
      const mileage = rawMileage !== null && /^\d+$/.test(rawMileage) ? Number(rawMileage) : null;
      rentLinkValid =
        !!token &&
        (rawMileage === null ||
          (mileage !== null && Number.isFinite(mileage) && Number.isInteger(mileage)));
      if (rentLinkValid) {
        if (mileage !== null) this.session.setLastMileage(mileage);
        if (token) this.session.setToken(token);
        if (applicationId) this.session.setApplicationId(applicationId);
      }
    }

    const storedType = this.session.flowType();
    const flowType = isValidFlowType(typeParam)
      ? typeParam
      : isValidFlowType(storedType)
        ? storedType
        : null;

    if (!isRentLink) {
      if (token) this.session.setToken(token);
      if (serviceId) this.session.setServiceId(serviceId);
      if (applicationId) this.session.setApplicationId(applicationId);
    }
    if (isNewDeepLink) {
      if (fcm !== null) {
        this.session.setFcm(fcm);
      } else {
        this.session.clearFcm();
      }
    }
    if (number) {
      this.session.setMainPhone(number);
    } else if (phone) {
      this.session.setMainPhone(phone);
    }
    if (additional) this.session.setAdditionalPhone(additional);

    if (flowType && rentLinkValid) {
      if (isValidFlowType(typeParam)) this.session.setFlowType(typeParam);
      this.document.defaultView?.history.replaceState(
        null,
        '',
        this.document.defaultView.location.pathname,
      );
      this.router.navigate([FLOW_ROUTES[flowType]]);
    } else {
      this.router.navigate(['/invalid-link']);
    }
  }
}
